import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowUpRight, FiMail, FiX } = FiIcons;
const STORAGE_KEY = 'dadda-cool-newsletter-popup-shown';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

function shouldShowPopup() {
  try {
    const lastShown = Number(localStorage.getItem(STORAGE_KEY) || 0);
    return Date.now() - lastShown >= DAY_IN_MS;
  } catch {
    return true;
  }
}

function rememberPopup() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // The popup still works when browser storage is unavailable.
  }
}

export default function NewsletterPopup() {
  const [visible, setVisible] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!shouldShowPopup()) return undefined;

    const timer = window.setTimeout(() => {
      rememberPopup();
      setVisible(true);
    }, 1400);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setVisible(false);
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="newsletter-popup-backdrop"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setVisible(false);
          }}
        >
          <motion.aside
            className="newsletter-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="newsletter-popup-title"
            initial={reduceMotion ? false : { opacity: 0, y: 28, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              className="newsletter-popup-close"
              onClick={() => setVisible(false)}
              aria-label="Close newsletter invitation"
            >
              <SafeIcon icon={FiX} />
            </button>

            <span className="newsletter-popup-icon" aria-hidden="true">
              <SafeIcon icon={FiMail} />
            </span>

            <p className="newsletter-popup-eyebrow">From Dadda Cool</p>
            <h2 id="newsletter-popup-title">Fresh drops, no noise.</h2>
            <p className="newsletter-popup-description">
              Get new music, creator gear, AI discoveries and hand-picked
              lifetime software deals delivered straight to your inbox.
            </p>

            <a
              className="newsletter-popup-action"
              href="https://sendfox.com/daddacool"
              target="_blank"
              rel="noreferrer"
              onClick={() => setVisible(false)}
            >
              <span>Join the newsletter</span>
              <SafeIcon icon={FiArrowUpRight} />
            </a>

            <small>Curated personally. Leave whenever you like.</small>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}